import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  ArrowLeft,
  Compass,
  CalendarIcon,
  Bell,
  User,
  LogOut,
  Save,
  Check,
  Mail,
  BookOpen,
  Sparkles,
  Send,
} from "lucide-react";
import { format, parse } from "date-fns";
import { toast } from "sonner";

const focusOptions = [
  { id: "general", label: "General Life", icon: "🌟" },
  { id: "career", label: "Career", icon: "💼" },
  { id: "relationships", label: "Relationships", icon: "❤️" },
  { id: "finance", label: "Finance", icon: "💰" },
  { id: "health", label: "Health", icon: "🧘" },
  { id: "spirituality", label: "Spirituality", icon: "✨" },
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [dateOfBirth, setDateOfBirth] = useState(
    user?.date_of_birth ? parse(user.date_of_birth, "yyyy-MM-dd", new Date()) : null
  );
  const [selectedFocusAreas, setSelectedFocusAreas] = useState(user?.focus_areas || []);
  const [notificationTime, setNotificationTime] = useState(user?.notification_time || "07:00");
  const [notificationsEnabled, setNotificationsEnabled] = useState(user?.notifications_enabled ?? true);
  const [saving, setSaving] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [digestTime, setDigestTime] = useState("07:00");
  const [digestIncludes, setDigestIncludes] = useState(["guidance", "quote", "affirmation"]);
  const [savingDigest, setSavingDigest] = useState(false);
  const [digestLoaded, setDigestLoaded] = useState(false);

  // Fetch digest preferences on mount
  useState(() => {
    api.get("/user/digest-preferences").then(r => {
      const d = r.data;
      setDigestEnabled(d.email_digest_enabled ?? false);
      setDigestTime(d.digest_time ?? "07:00");
      setDigestIncludes(d.digest_includes ?? ["guidance", "quote", "affirmation"]);
      setDigestLoaded(true);
    }).catch(() => setDigestLoaded(true));
  }, []);

  const toggleFocusArea = (id) => {
    setSelectedFocusAreas((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const handleSaveProfile = async () => {
    if (!firstName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!dateOfBirth) {
      toast.error("Please select your date of birth");
      return;
    }
    if (selectedFocusAreas.length === 0) {
      toast.error("Please select at least one focus area");
      return;
    }

    setSaving(true);
    try {
      const response = await api.put("/user/profile", {
        first_name: firstName,
        date_of_birth: format(dateOfBirth, "yyyy-MM-dd"),
        focus_areas: selectedFocusAreas,
        notification_time: notificationTime,
      });
      setUser(response.data);
      toast.success("Profile updated!");
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      const response = await api.put("/user/notifications", {
        notification_time: notificationTime,
        notifications_enabled: notificationsEnabled,
      });
      setUser(response.data);
      toast.success("Notification settings updated!");
    } catch (error) {
      console.error("Error saving notifications:", error);
      toast.error("Failed to save notification settings");
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const toggleDigestInclude = (item) => {
    setDigestIncludes(prev =>
      prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]
    );
  };

  const handleSaveDigest = async () => {
    setSavingDigest(true);
    try {
      await api.put("/user/digest-preferences", {
        email_digest_enabled: digestEnabled,
        digest_time: digestTime,
        digest_includes: digestIncludes,
      });
      toast.success("Email digest preferences saved!");
    } catch (error) {
      toast.error("Failed to save digest preferences");
    } finally {
      setSavingDigest(false);
    }
  };

  const [sendingTestDigest, setSendingTestDigest] = useState(false);
  const handleSendTestDigest = async () => {
    setSendingTestDigest(true);
    try {
      await api.post("/user/digest/send");
      toast.success("Test digest email sent! Check your inbox.");
    } catch (error) {
      toast.error("Failed to send test digest");
    } finally {
      setSendingTestDigest(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#070B14" }} data-testid="profile-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#070B14]/70 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A16207] flex items-center justify-center">
              <Compass className="w-4 h-4 text-[#070B14]" />
            </div>
            <span className="font-display text-xl text-white">Profile</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Profile Header */}
        <div className="flex items-center gap-4 animate-fade-in-up">
          <img
            src={user?.picture || `https://ui-avatars.com/api/?name=${user?.first_name || user?.name}&background=D4AF37&color=070B14&size=128`}
            alt="Profile"
            className="w-20 h-20 rounded-full border-2 border-[#D4AF37]"
          />
          <div>
            <h1 className="font-display text-2xl text-white">{user?.first_name || user?.name}</h1>
            <p className="text-white/60 font-body text-sm">{user?.email}</p>
          </div>
        </div>

        {/* Personal Info */}
        <div className="glass-card p-6 space-y-6 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-display text-xl text-white">Personal Information</h2>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <label className="block text-sm text-white/70 font-body">Your Name</label>
            <Input
              data-testid="profile-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="custom-input"
            />
          </div>

          {/* DOB */}
          <div className="space-y-2">
            <label className="block text-sm text-white/70 font-body">Date of Birth</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  data-testid="profile-dob-trigger"
                  variant="outline"
                  className="custom-input w-full justify-start text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4 text-[#D4AF37]" />
                  {dateOfBirth ? format(dateOfBirth, "PPP") : <span className="text-white/30">Select date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#0A101D] border-white/10" align="start">
                <Calendar
                  mode="single"
                  selected={dateOfBirth}
                  onSelect={setDateOfBirth}
                  disabled={(date) => date > new Date() || date < new Date("1900-01-01")}
                  initialFocus
                  captionLayout="dropdown-buttons"
                  fromYear={1940}
                  toYear={new Date().getFullYear()}
                  className="bg-[#0A101D] text-white"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Focus Areas */}
          <div className="space-y-3">
            <label className="block text-sm text-white/70 font-body">Focus Areas</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {focusOptions.map((option) => (
                <button
                  key={option.id}
                  data-testid={`profile-focus-${option.id}`}
                  onClick={() => toggleFocusArea(option.id)}
                  className={`p-3 rounded-xl border transition-all duration-300 text-left ${
                    selectedFocusAreas.includes(option.id)
                      ? "focus-chip-selected border-[#D4AF37]"
                      : "border-white/10 bg-white/5 hover:bg-white/10"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{option.icon}</span>
                    <span className="font-body text-xs text-white">{option.label}</span>
                    {selectedFocusAreas.includes(option.id) && (
                      <Check className="w-3 h-3 text-[#D4AF37] ml-auto" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <Button
            data-testid="save-profile"
            onClick={handleSaveProfile}
            disabled={saving}
            className="btn-gold w-full"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-[#070B14] border-t-transparent rounded-full animate-spin" />
                Saving...
              </span>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Profile
              </>
            )}
          </Button>
        </div>

        {/* Notifications */}
        <div className="glass-card p-6 space-y-6 animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-display text-xl text-white">Notifications</h2>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-body">Daily Guidance Notifications</p>
              <p className="text-white/50 text-sm font-body">Receive reminders for your daily guidance</p>
            </div>
            <Switch
              data-testid="notifications-toggle"
              checked={notificationsEnabled}
              onCheckedChange={setNotificationsEnabled}
            />
          </div>

          {notificationsEnabled && (
            <div className="space-y-2">
              <label className="block text-sm text-white/70 font-body">Notification Time</label>
              <Input
                data-testid="notification-time"
                type="time"
                value={notificationTime}
                onChange={(e) => setNotificationTime(e.target.value)}
                className="custom-input"
              />
            </div>
          )}

          <Button
            data-testid="save-notifications"
            onClick={handleSaveNotifications}
            disabled={savingNotifications}
            className="btn-secondary w-full"
          >
            {savingNotifications ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </span>
            ) : (
              "Save Notification Settings"
            )}
          </Button>
        </div>

        {/* Daily Email Digest */}
        <div className="glass-card p-6 space-y-6 animate-fade-in-up" style={{ animationDelay: "0.25s" }}>
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-display text-xl text-white">Daily Email Digest</h2>
          </div>
          <p className="text-white/50 font-body text-sm">
            Receive a beautiful morning email with your guidance headline, Sage quote, and affirmation.
          </p>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-body">Enable Daily Digest</p>
              <p className="text-white/50 text-sm font-body">Get your daily wisdom delivered to your inbox</p>
            </div>
            <Switch
              data-testid="digest-toggle"
              checked={digestEnabled}
              onCheckedChange={setDigestEnabled}
            />
          </div>

          {digestEnabled && (
            <>
              <div className="space-y-2">
                <label className="block text-sm text-white/70 font-body">Delivery Time</label>
                <Input
                  data-testid="digest-time"
                  type="time"
                  value={digestTime}
                  onChange={(e) => setDigestTime(e.target.value)}
                  className="custom-input"
                />
              </div>

              <div className="space-y-3">
                <label className="block text-sm text-white/70 font-body">Include in Digest</label>
                <div className="space-y-2">
                  {[
                    { id: "guidance", label: "Daily Guidance Headline", icon: <Sparkles className="w-4 h-4 text-[#C4B5FD]" /> },
                    { id: "quote", label: "Quote of the Day Sage", icon: <BookOpen className="w-4 h-4 text-[#D4AF37]" /> },
                    { id: "affirmation", label: "Daily Affirmation", icon: <Sparkles className="w-4 h-4 text-green-400" /> },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      data-testid={`digest-include-${opt.id}`}
                      onClick={() => toggleDigestInclude(opt.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${
                        digestIncludes.includes(opt.id)
                          ? "border-[#D4AF37]/40 bg-[#D4AF37]/5"
                          : "border-white/10 bg-white/5 hover:bg-white/10"
                      }`}
                    >
                      {opt.icon}
                      <span className="font-body text-sm text-white flex-1 text-left">{opt.label}</span>
                      {digestIncludes.includes(opt.id) && <Check className="w-4 h-4 text-[#D4AF37]" />}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          <Button
            data-testid="save-digest"
            onClick={handleSaveDigest}
            disabled={savingDigest}
            className="btn-secondary w-full"
          >
            {savingDigest ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </span>
            ) : (
              "Save Digest Preferences"
            )}
          </Button>

          {digestEnabled && (
            <Button
              data-testid="send-test-digest"
              onClick={handleSendTestDigest}
              disabled={sendingTestDigest}
              variant="outline"
              className="w-full border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
            >
              {sendingTestDigest ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                  Sending...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Send Test Digest Now
                </span>
              )}
            </Button>
          )}
        </div>

        {/* Logout */}
        <div className="glass-card p-6 animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
          <Button
            data-testid="logout-btn"
            onClick={handleLogout}
            variant="outline"
            className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-white/30 font-body text-center px-4 animate-fade-in-up" style={{ animationDelay: "0.4s" }}>
          SoulGuide provides reflective, motivational, and spiritual lifestyle guidance for personal inspiration only.
          It does not replace medical, legal, mental health, or financial advice.
        </p>
      </main>
    </div>
  );
};

export default ProfilePage;
